import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowUp, ArrowDown, Loader2, Archive, MessageSquare, Download, FileText, FileImage, FileVideo, File } from 'lucide-react';
import { cn } from '@/lib/utils';
import { withAppTimeZone } from '@/lib/timezone';
import { ScrollArea } from '@/components/ui/scroll-area';
import { fetchArchivedMessagesAround, fetchArchivedMessagesByPhone, downloadArchivedMedia, fetchArchivedMessagesBefore, fetchArchivedMessagesAfter } from '@/lib/api/archived-messages';

const CHUNK_SIZE = 20;

/** Returns a lucide icon component matching the MIME type. */
function mediaIcon(mime) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('video/')) return FileVideo;
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('word') || mime.includes('text')) return FileText;
  return File;
}

/**
 * Compact download button for media messages with a media_url.
 *
 * `canExport` is archive:manage. Without it the chip still renders — archive:view entitles the
 * reader to know a file exists — but inert, with no click target and no download glyph. Defaults
 * false so a caller that forgets to thread the prop fails closed. The permission is read once at
 * the page level and passed down: this component renders once per media hit, and subscribing in
 * each instance would attach a window listener per row.
 */
function MediaDownloadButton({ message, canExport = false }) {
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState('');

  const mediaUrl = String(message.media_url || '').trim();
  const mime = String(message.mime || '').toLowerCase();
  const caption = String(message.caption || '').trim();
  const rawName = decodeURIComponent(mediaUrl.split('/').pop() || 'file');
  const label = caption || rawName;
  const IconComp = mediaIcon(mime);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    setDlError('');
    try {
      await downloadArchivedMedia({ mediaUrl, fileName: rawName });
    } catch (err) {
      setDlError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const body = (
    <>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 transition-colors group-hover/dl:bg-amber-500/25 dark:bg-amber-500/20 dark:text-amber-400">
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <IconComp className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-foreground">{label}</p>
        {mime && <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{mime.split('/').pop()}</p>}
      </div>
    </>
  );

  if (!canExport) {
    return (
      <div className="mt-1 flex flex-col gap-1">
        <div
          className="flex items-center gap-2.5 rounded-xl border border-amber-400/25 bg-amber-500/5 px-3 py-2.5 text-start opacity-70 dark:border-amber-400/15 dark:bg-amber-500/8"
          title="Downloading archived media requires Archive → Manage"
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="group/dl flex items-center gap-2.5 rounded-xl border border-amber-400/40 bg-amber-500/8 px-3 py-2.5 text-start transition-all hover:border-amber-400/70 hover:bg-amber-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-400/25 dark:bg-amber-500/10 dark:hover:border-amber-400/50 dark:hover:bg-amber-500/20"
      >
        {body}
        <Download className="h-3.5 w-3.5 shrink-0 text-amber-500 opacity-70 transition-opacity group-hover/dl:opacity-100" />
      </button>
      {dlError && <p className="text-[10px] text-destructive">{dlError}</p>}
    </div>
  );
}

/**
 * Formats a timestamp (unix seconds or ISO string) to a readable date-time string.
 */
function formatTime(timestamp, messageDateStr) {
  const raw = timestamp || messageDateStr;
  if (!raw) return '';
  try {
    const ms = typeof raw === 'number' ? raw * 1000 : new Date(raw).getTime();
    if (Number.isNaN(ms)) return '';
    return new Date(ms).toLocaleString(undefined, withAppTimeZone({
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }));
  } catch {
    return '';
  }
}

/**
 * Single message bubble inside the context view.
 * When `isAnchor` is true, renders with a highlighted golden ring.
 */
function ContextBubble({ message, isAnchor, anchorRef, canExport }) {
  const fromMe = Boolean(message.from_me);
  const text = String(message.data || message.caption || '').trim();
  const time = formatTime(message.timestamp, message.message_date);
  const chatName = String(message.chat_name || message.patient?.displayName || '').trim();
  const sender = String(message.sender || message.phone || '').trim();
  const hasMedia = Boolean(message.media);
  const isSticker = Boolean(message.sticker);
  const mime = String(message.mime || '').toLowerCase();
  const isImage = hasMedia && (mime.startsWith('image/') || Boolean(message.thumb));

  const initials = fromMe
    ? sender.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('')
    : chatName.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');

  return (
    <div
      ref={isAnchor ? anchorRef : undefined}
      className={cn(
        'group flex items-end gap-2',
        fromMe ? 'justify-end' : 'justify-start',
        isAnchor && 'scroll-mt-8',
      )}
    >
      {!fromMe && (
        <span className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ring-1',
          isAnchor
            ? 'bg-amber-400/30 text-amber-700 ring-amber-500/60 dark:bg-amber-400/25 dark:text-amber-300 dark:ring-amber-400/50'
            : 'bg-amber-500/15 text-amber-700 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25',
        )}>
          {initials}
        </span>
      )}

      <article className={cn(
        'min-w-0 rounded-2xl border px-4 py-3 text-start transition-all duration-300',
        'max-w-[85%]',
        fromMe
          ? 'border-amber-400/20 bg-amber-500/8 dark:border-amber-400/25 dark:bg-amber-500/12'
          : 'border-amber-400/30 bg-amber-50/60 shadow-sm dark:border-amber-400/20 dark:bg-amber-950/20 dark:shadow-none',
        isAnchor && [
          'border-amber-500/70 ring-2 ring-amber-400/50 ring-offset-1 ring-offset-background',
          'bg-amber-400/10 dark:bg-amber-400/15',
          'shadow-[0_0_16px_rgba(251,191,36,0.25)]',
        ],
      )}>
        {/* Anchor label */}
        {isAnchor && (
          <div className="mb-2 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-500/30 dark:text-amber-300">
              <MessageSquare className="h-2.5 w-2.5" />
              Search result
            </span>
          </div>
        )}

        {!fromMe && chatName && (
          <p className="mb-1 max-w-48 truncate text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {chatName}
          </p>
        )}
        {fromMe && sender && (
          <p className="mb-1 max-w-48 truncate text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {sender}
          </p>
        )}

        {isSticker && <p className="text-xs italic text-muted-foreground">[Sticker]</p>}
        {isImage && !text && <p className="text-xs italic text-muted-foreground">[Image]</p>}
        {hasMedia && !isImage && !isSticker && message.media_url && (
          <MediaDownloadButton message={message} canExport={canExport} />
        )}
        {hasMedia && !isImage && !isSticker && !message.media_url && !text && (
          <p className="text-xs italic text-muted-foreground">[{mime || 'Media'}]</p>
        )}
        {text && !hasMedia && (
          <p className="wrap-break-word text-sm leading-relaxed text-foreground">{text}</p>
        )}
        <div className={cn('mt-1 whitespace-nowrap text-[11px] text-muted-foreground', fromMe ? 'text-right' : 'text-left')}>
          {time}
        </div>
      </article>

      {fromMe && (
        <span className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ring-1',
          isAnchor
            ? 'bg-amber-400/30 text-amber-700 ring-amber-500/60 dark:bg-amber-400/25 dark:text-amber-300 dark:ring-amber-400/50'
            : 'bg-amber-500/15 text-amber-700 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25',
        )}>
          {initials}
        </span>
      )}
    </div>
  );
}

/** Small loading indicator pill shown at top/bottom while fetching more. */
function LoadingPill({ label }) {
  return (
    <div className="flex justify-center py-2">
      <div className="flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-50/70 px-4 py-1.5 text-xs text-amber-700 shadow-sm dark:border-amber-400/20 dark:bg-amber-950/30 dark:text-amber-300">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{label}</span>
      </div>
    </div>
  );
}

/**
 * Panel showing the full conversation context around a specific message,
 * with infinite scroll (auto-load older/newer messages on scroll).
 *
 * @param {object} props
 * @param {string} props.phone - Phone number for the API call (without leading +)
 * @param {string|number} props.anchorMessageId - message_id of the anchor message
 * @param {object} props.anchorMessage - The original search-result message (metadata fallback)
 * @param {() => void} props.onClose - Called when the user goes back to search results
 */
export function ConversationContextView({ phone, anchorMessageId, anchorMessage, onClose, canExport = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Infinite scroll direction flags
  const [hasMoreAbove, setHasMoreAbove] = useState(false);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [loadingAbove, setLoadingAbove] = useState(false);
  const [loadingBelow, setLoadingBelow] = useState(false);

  // Loading-more flags (prevent double-fetches)
  const loadingAboveRef = useRef(false);
  const loadingBelowRef = useRef(false);

  // Always-current ref to messages array (avoids stale closures in callbacks)
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Scroll anchoring: save scrollHeight before prepending so the viewport doesn't jump
  const prevScrollHeightRef = useRef(0);
  const isPrependRef = useRef(false);

  const scrollContainerRef = useRef(null);
  const anchorRef = useRef(null);
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);

  // Whether it's the very first render after initial load (to trigger scroll-to-anchor once)
  const didScrollToAnchor = useRef(false);

  const chatName = String(
    anchorMessage?.chat_name || anchorMessage?.patient?.displayName || phone || '',
  ).trim();

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setMessages([]);
    setHasMoreAbove(false);
    setHasMoreBelow(false);
    setLoadingAbove(false);
    setLoadingBelow(false);
    loadingAboveRef.current = false;
    loadingBelowRef.current = false;
    didScrollToAnchor.current = false;

    fetchArchivedMessagesAround({ phone, messageId: anchorMessageId })
      .then((result) => {
        if (cancelled) return;
        const msgs = result.messages.reverse();
        setMessages(msgs);
        setHasMoreAbove(Number(result.before) > 0);
        setHasMoreBelow(Number(result.after) > 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load conversation context');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [anchorMessageId]);

  // ── Scroll to anchor once after initial load ───────────────────────────────
  useEffect(() => {
    if (!loading && messages.length > 0 && anchorRef.current && !didScrollToAnchor.current) {
      didScrollToAnchor.current = true;
      const timer = setTimeout(() => {
        anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading, messages.length]);

  // ── Scroll anchoring: restore position after prepending older messages ──────
  useLayoutEffect(() => {
    if (isPrependRef.current && scrollContainerRef.current) {
      const delta = scrollContainerRef.current.scrollHeight - prevScrollHeightRef.current;
      scrollContainerRef.current.scrollTop += delta;
      isPrependRef.current = false;
    }
  }, [messages]); // runs synchronously after DOM update

  // ── Load older messages (scroll up) ───────────────────────────────────────
  const loadAbove = useCallback(async () => {
    if (loadingAboveRef.current || !hasMoreAbove) return;
    const msgs = messagesRef.current;
    if (msgs.length === 0) return;

    const oldestMsg = msgs[0];
    const oldestId = oldestMsg?.message_id ?? oldestMsg?._id ?? oldestMsg?.key_id;
    if (oldestId === undefined || oldestId === null || oldestId === '') return;

    loadingAboveRef.current = true;
    setLoadingAbove(true);

    try {
      const result = await fetchArchivedMessagesBefore({ phone, messageId: oldestId });
      const newMsgs = result.messages.reverse();

      if (newMsgs.length === 0) {
        setHasMoreAbove(false);
      } else {
        // Save scroll height synchronously right before state update
        if (scrollContainerRef.current) {
          prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
        }
        isPrependRef.current = true;
        setMessages((prev) => [...newMsgs, ...prev]);
      }
    } catch (err) {
      console.error('[ConversationContextView] loadAbove error:', err);
    } finally {
      setLoadingAbove(false);
      loadingAboveRef.current = false;
    }
  }, [hasMoreAbove, phone]);

  // ── Load newer messages (scroll down) ─────────────────────────────────────
  const loadBelow = useCallback(async () => {
    if (loadingBelowRef.current || !hasMoreBelow) return;
    const msgs = messagesRef.current;
    if (msgs.length === 0) return;

    const newestMsg = msgs[msgs.length - 1];
    const newestId = newestMsg?.message_id ?? newestMsg?._id ?? newestMsg?.key_id;
    if (newestId === undefined || newestId === null || newestId === '') return;

    loadingBelowRef.current = true;
    setLoadingBelow(true);

    try {
      const result = await fetchArchivedMessagesAfter({ phone, messageId: newestId });
      const newMsgs = result.messages.reverse();

      if (newMsgs.length === 0) {
        setHasMoreBelow(false);
      } else {
        setMessages((prev) => [...prev, ...newMsgs]);
      }
    } catch (err) {
      console.error('[ConversationContextView] loadBelow error:', err);
    } finally {
      setLoadingBelow(false);
      loadingBelowRef.current = false;
    }
  }, [hasMoreBelow, phone]);

  // ── IntersectionObserver: auto-trigger on sentinel visibility ─────────────
  useEffect(() => {
    if (loading || messages.length === 0) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target === topSentinelRef.current) {
            loadAbove();
          } else if (entry.target === bottomSentinelRef.current) {
            loadBelow();
          }
        }
      },
      { root: container, threshold: 0 },
    );

    const top = topSentinelRef.current;
    const bottom = bottomSentinelRef.current;
    if (top) observer.observe(top);
    if (bottom) observer.observe(bottom);

    return () => observer.disconnect();
  }, [loading, messages.length, loadAbove, loadBelow]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Header ── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Results</span>
        </button>

        <div className="h-4 w-px bg-border" />

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 shadow-sm">
            <Archive className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{chatName || phone}</p>
            <p className="text-[11px] text-muted-foreground font-mono">{phone}</p>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <ScrollArea viewportRef={scrollContainerRef} className="min-h-0 flex-1">

        {/* Initial loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
            <p className="text-sm text-muted-foreground">Loading conversation context…</p>
          </div>
        )}

        {error && !loading && (
          <div className="mx-5 mt-5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && !error && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <Archive className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No context available</p>
          </div>
        )}

        {!loading && !error && messages.length > 0 && (
          <div className="flex flex-col gap-3 px-4 py-4">

            {/* ↑ Top sentinel — IntersectionObserver trigger for loadAbove */}
            <div ref={topSentinelRef} className="h-1 w-full" aria-hidden />

            {/* Loading pill (top) */}
            {loadingAbove && <LoadingPill label="Loading older messages…" />}

            {/* Beginning-of-conversation marker */}
            {!hasMoreAbove && !loadingAbove && (
              <p className="py-1 text-center text-[11px] text-muted-foreground">
                — Beginning of conversation —
              </p>
            )}

            {/* Message list */}
            {messages.map((msg, i) => {
              const isAnchor =
                anchorMessageId != null && (
                  msg._id === anchorMessageId ||
                  String(msg.message_id) === String(anchorMessageId) ||
                  msg.key_id === anchorMessageId
                );
              return (
                <ContextBubble
                  key={msg._id || msg.key_id || msg.message_id || i}
                  message={msg}
                  isAnchor={Boolean(isAnchor)}
                  anchorRef={anchorRef}
                  canExport={canExport}
                />
              );
            })}

            {/* Loading pill (bottom) */}
            {loadingBelow && <LoadingPill label="Loading newer messages…" />}

            {/* End-of-conversation marker */}
            {!hasMoreBelow && !loadingBelow && (
              <p className="py-1 text-center text-[11px] text-muted-foreground">
                — End of conversation —
              </p>
            )}

            {/* ↓ Bottom sentinel — IntersectionObserver trigger for loadBelow */}
            <div ref={bottomSentinelRef} className="h-1 w-full" aria-hidden />
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
